import { jsxs, jsx } from 'react/jsx-runtime';
import { mediaDesktop } from '../../constants/styled-utils.js';
import { renderCssBasedOnColorScheme } from '../../theme/renderCssBasedOnColorScheme.js';
import { cssVar } from '../../theme/variables.js';
import { styled, css } from 'styled-components';
import { useGesture } from '../../../node_modules/@use-gesture/react/dist/use-gesture-react.esm.js';
import { useState, useRef, useEffect } from 'react';
import { createLogger } from '@fabric-app/utils/logging';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { Spinner } from '../Spinner.js';

const getBounds = (containerBox, itemBox) => {
  const top = containerBox.top < itemBox.top ? itemBox.top : containerBox.top;
  const left = containerBox.left < itemBox.left ? itemBox.left : containerBox.left;
  const right = containerBox.right > itemBox.right ? itemBox.right : containerBox.right;
  const bottom = Math.min(containerBox.bottom, itemBox.bottom);
  return {
    left,
    top,
    right,
    bottom
  };
};
const getDeltaForItemToSnapWithinBounds = (itemRect, bounds) => {
  const deltaX = bounds.left < itemRect.left ? bounds.left - itemRect.left : bounds.right > itemRect.right ? bounds.right - itemRect.right : 0;
  const deltaY = bounds.top < itemRect.top ? bounds.top - itemRect.top : bounds.bottom > itemRect.bottom ? bounds.bottom - itemRect.bottom : 0;
  return {
    deltaX,
    deltaY
  };
};
const logger = createLogger("PinchZoomImageV2", true);
const MIN_SCALE = 1;
const PinchZoomImageV2 = ({ height, width, srcs }) => {
  const [dimensions, setDimensions] = useState({
    width,
    height
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [state, setState] = useState({
    scale: 1,
    isManupulating: false,
    x: 0,
    y: 0,
    isFocusMode: false,
    lastFocusModeExit: 0
  });
  const [displayHqImage, setDisplayHqImage] = useState(false);
  useRunOnce(() => {
    setDisplayHqImage(true);
  }, {
    condition: state.scale > 2
  });
  const domTargetRef = useRef(null);
  const containerRef = useRef(null);
  const boundBoxRef = useRef(null);
  const snapToBounds = () => {
    const containerBox = containerRef.current?.getBoundingClientRect();
    const boundItemBox = boundBoxRef.current?.getBoundingClientRect();
    const itemBox = domTargetRef.current?.getBoundingClientRect();
    if (!containerBox || !itemBox || !boundItemBox) {
      logger.error("Bounds not found");
      return;
    }
    setState((prev) => {
      const bounds = getBounds(containerBox, boundItemBox);
      const { deltaX, deltaY } = getDeltaForItemToSnapWithinBounds(itemBox, bounds);
      const nextScale = prev.scale - MIN_SCALE < 0.05 ? MIN_SCALE : prev.scale;
      const nextFocusMode = nextScale <= MIN_SCALE ? false : prev.isFocusMode;
      return {
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY,
        scale: nextScale,
        isFocusMode: nextScale <= MIN_SCALE ? false : prev.isFocusMode,
        lastFocusModeExit: nextFocusMode ? prev.lastFocusModeExit : Date.now()
      };
    });
    setTimeout(() => {
      setState((prev) => ({
        ...prev,
        isManupulating: false
      }));
    }, 200);
  };
  useGesture({
    onPinchStart: () => {
      setState((prev) => ({
        ...prev,
        isManupulating: true
      }));
    },
    onDragStart: () => {
      setState((prev) => ({
        ...prev,
        isManupulating: true
      }));
    },
    onDrag: ({ cancel, pinching, delta: [x2, y2] }) => {
      if (pinching || state.scale <= 1) {
        cancel();
        return;
      }
      const containerBox = containerRef.current?.getBoundingClientRect();
      const itemBox = domTargetRef.current?.getBoundingClientRect();
      if (!containerBox || !itemBox) {
        logger.error("Bounds not found");
        return;
      }
      setState((s) => ({
        ...s,
        x: s.x + x2,
        y: s.y + y2,
        isManupulating: true
      }));
    },
    onDragEnd: ({ distance: [dx, dy], elapsedTime }) => {
      if (elapsedTime < 100 && dx < 3 && dy < 3) {
        setState((s) => {
          return {
            ...s,
            isManupulating: false,
            x: 0,
            y: 0,
            scale: 1
          };
        });
      }
      setTimeout(snapToBounds, 100);
    },
    onPinch: (c) => {
      const { origin: [ox, oy], delta: [d] } = c;
      const scaleDelta = d;
      setState((prevState) => {
        const itemRect = domTargetRef.current?.getBoundingClientRect();
        const containerBox = containerRef.current?.getBoundingClientRect();
        const boundItemBox = boundBoxRef.current?.getBoundingClientRect();
        if (!itemRect || !containerBox || !boundItemBox) {
          logger.error("Bounds not found");
          return prevState;
        }
        const nextScale = prevState.scale + scaleDelta;
        if (nextScale < MIN_SCALE - 0.05 || nextScale > 6) {
          return prevState;
        }
        const bounds = getBounds(containerBox, boundItemBox);
        const currentWidth = itemRect.width;
        const futureWidth = itemRect.width / prevState.scale * nextScale;
        const diffWidth = futureWidth - currentWidth;
        const relativePositionX = ox / (bounds.right - bounds.left);
        const preDeltaX = diffWidth / 2 * (relativePositionX - 0.5) * -2;
        const currentHeight = itemRect.height;
        const futureHeight = itemRect.height / prevState.scale * nextScale;
        const diffHeight = futureHeight - currentHeight;
        const relativePositionY = oy / (bounds.bottom - bounds.left);
        const preDeltaY = diffHeight / 2 * (relativePositionY - 0.5) * -2;
        const nextX = prevState.x + preDeltaX;
        const nextY = prevState.y + preDeltaY;
        return {
          ...prevState,
          scale: nextScale,
          x: nextX,
          y: nextY,
          isManupulating: true,
          isFocusMode: d > 0 && nextScale > MIN_SCALE ? true : prevState.isFocusMode
        };
      });
    },
    onPinchEnd: () => {
      setTimeout(snapToBounds, 100);
    }
  }, {
    target: containerRef,
    pinch: {
      rubberband: true,
      from: () => {
        return [
          state.scale,
          0
        ];
      }
    },
    drag: {
      enabled: state.scale > 1 || state.isFocusMode
    }
  });
  const x = useMotionValue(state.x);
  const y = useMotionValue(state.y);
  const scale = useMotionValue(state.scale);
  useEffect(() => {
    const duration = state.isManupulating ? 0.05 : 0.1;
    const ctX = animate(x, state.x, {
      duration,
      ease: "linear"
    });
    const ctY = animate(y, state.y, {
      duration,
      ease: "linear"
    });
    const ctS = animate(scale, state.scale, {
      duration,
      ease: "linear"
    });
    return () => {
      ctX.stop();
      ctY.stop();
      ctS.stop();
    };
  }, [
    state,
    x,
    y,
    scale
  ]);
  const styleCssVars = {
    "--pinch-zoom-image-height": `${dimensions.height}px`,
    "--pinch-zoom-image-width": `${dimensions.width}px`,
    "--pinch-zoom-image-aspect-ratio": `${dimensions.width / dimensions.height}`
  };
  const panIgnore = state.isManupulating || state.isFocusMode ? "xy" : false;
  return /* @__PURE__ */ jsxs(Container, {
    ref: containerRef,
    "data-pan-id-ignore-expanded-resource": panIgnore,
    "data-pan-id-ignore-resource-drag-resize": panIgnore,
    onMouseUp: () => {
      if (state.scale <= 1 && !state.isManupulating && !state.isFocusMode && Date.now() - state.lastFocusModeExit > 300) {
        setState((s) => ({
          ...s,
          isFocusMode: !s.isFocusMode,
          isManupulating: !s.isFocusMode
        }));
      }
    },
    style: state.isFocusMode ? {
      ...styleCssVars,
      position: "fixed",
      background: "black",
      top: 0,
      zIndex: 1e3
    } : {
      ...styleCssVars,
      height: "100%"
    },
    children: [
      !isLoaded && /* @__PURE__ */ jsx(LoadingState, {
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 16,
          color: cssVar["color-text-tertiary"]
        })
      }),
      /* @__PURE__ */ jsxs(Content, {
        as: motion.div,
        style: {
          scale,
          x,
          y,
          opacity: isLoaded ? 1 : 0,
          touchAction: state.isFocusMode || state.isManupulating ? "none" : "pan-x"
        },
        ref: domTargetRef,
        children: [
          /* @__PURE__ */ jsx(Image, {
            as: motion.img,
            layout: true,
            transition: {
              duration: 0.2
            },
            src: srcs.default,
            alt: "zoomed",
            onLoad: (e) => {
              setIsLoaded(true);
              setDimensions({
                width: e.currentTarget.naturalWidth,
                height: e.currentTarget.naturalHeight
              });
            }
          }),
          displayHqImage && srcs.zoomed && /* @__PURE__ */ jsx(Image, {
            as: motion.img,
            layout: true,
            transition: {
              duration: 0.2
            },
            src: srcs.zoomed,
            alt: "zoomed",
            style: {
              zIndex: 5
            }
          })
        ]
      }),
      /* @__PURE__ */ jsx(BoundingBox, {
        ref: boundBoxRef,
        style: {
          transform: `translate(-50%, -50%) scale(${state.scale})`
        }
      })
    ]
  });
};
const Container = styled.div.withConfig({
  displayName: "PinchZoomImageV2__Container",
  componentId: "sc-ccdbf79-0"
})([
  `position:relative;min-height:100px;max-height:100%;height:100%;width:100%;max-width:100%;will-change:height;flex-grow:1;display:flex;align-items:center;justify-content:center;overflow:hidden;`,
  ` `,
  `{padding:0.5rem;}`
], renderCssBasedOnColorScheme({
  light: css([
    `background:`,
    `;`
  ], cssVar["color-bg-tertiary"]),
  dark: css([
    `background:`,
    `;`
  ], cssVar["color-bg-primary"])
}), mediaDesktop);
const Content = styled(motion.div).withConfig({
  displayName: "PinchZoomImageV2__Content",
  componentId: "sc-ccdbf79-1"
})([
  `position:relative;width:100%;aspect-ratio:var(--pinch-zoom-image-aspect-ratio);`
]);
const BoundingBox = styled.div.withConfig({
  displayName: "PinchZoomImageV2__BoundingBox",
  componentId: "sc-ccdbf79-2"
})([
  `position:absolute;aspect-ratio:var(--pinch-zoom-image-aspect-ratio);top:50%;left:50%;pointer-events:none;z-index:100;width:100%;`
]);
const Image = styled.img.withConfig({
  displayName: "PinchZoomImageV2__Image",
  componentId: "sc-ccdbf79-3"
})([
  `width:100%;height:100%;position:absolute;top:0;left:0;border:none;background:transparent;outline:none;`
]);
const LoadingState = styled.div.withConfig({
  displayName: "PinchZoomImageV2__LoadingState",
  componentId: "sc-ccdbf79-4"
})([
  `position:absolute;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;z-index:100;`
]);

export { PinchZoomImageV2 };
//# sourceMappingURL=PinchZoomImageV2.js.map
